/**
 * useTutorActivityDetails — la fiche d'une activité ouverte par le tuteur hors live
 * (`<TutorActivityDetailsModal>`) : sa lecture, ses vues, ses gestes et leurs accusés.
 *
 * Le legacy est `student/activity/todo/TodoModal.js`, branche tuteur en modale, avec
 * `EditActivityTitleModal.js` et `components/QuizFirstMention.js`. Les mêmes appels, avec les mêmes
 * paramètres, dans le même ordre — `TUTOR_ACTIVITY_DETAILS_EQUIVALENCE` les tient, un test par ligne :
 *  - l'ouverture relit l'activité (`getActivities`, `with_logs`, `activity_ids`, `include_archived`), et
 *    la relit toutes les 5 s tant que le champion la corrige (`useCorrectionPolling`) ;
 *  - « Soumettre » (`finalizeActivitySilent`) puis `getStudent` et la relecture ; « Valider »
 *    (`tutorValidateActivity`, après la question) puis la relecture ; « Redonner » (`activityTodoAsTutor`) ;
 *  - « Archiver » relit l'activité (`getActivity`) avant de demander (verrouillée ou commencée par
 *    l'élève, elle ne s'archive pas), puis `deleteActivitiesTodo` ; « Restaurer »
 *    (`restoreArchivedActivities`) puis `getStudent` ;
 *  - « Modifier le titre » (`updateActivityTodo`, `material_title`) ; « Quiz d'abord · Retirer »
 *    (`deletePrerequisiteQuiz`) puis `getStudent` ; l'avis sur la correction du champion
 *    (`getActivityCorrectionFeedback` à l'ouverture d'une correction finie, `submitActivityCorrectionFeedback`).
 *
 * Hors live, rien ne part à la salle : les `newTodo` / `deleteTodo` / `tutorFinalizedActivity` du legacy
 * passent par `useLiveKitEventSender`, qui ne trouve pas de salle hors live et n'envoie rien.
 *
 * Les boîtes du legacy (`DialogM`) sont les décisions de la fiche ; ses `SnackBarM`, un `<Snackbar>`,
 * sans les ✅ / ⚠️ (le bandeau du livre porte son ton). Chaque écriture est mesurée (`gesture.*`).
 */
import * as amplitude from "@amplitude/analytics-react-native"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useWindowDimensions } from "react-native"
import { useDispatch, useSelector } from "react-redux"

import { cleanMediaUrl } from "../../../../../../data/constants/apiConstants"
import { activitiesService } from "../../../../../../data/services/activitiesService"
import { studentsService } from "../../../../../../data/services/studentsService"
import { buildPendingAttachmentsBody } from "../../../../../../data/utility/pendingAttachmentsUtils"
import { useCorrectionPolling } from "../../../../../../engine/activity/correction/useCorrectionPolling"
import { apiCall, apiFailureOf, type ApiService } from "../../../../../../engine/shared/apiCall"
import { type SnackbarProps, type SnackbarTone } from "../../../../../../design-system/molecules/Snackbar"
import { type TutorActivityDetailsModalProps } from "../../../../../../design-system/templates/TutorActivityDetailsModal"
import {
    activityStillListed,
    archiveAnswerFor,
    defaultTutorActivityDisplayMode,
    toUITutorActivityDetails,
    TUTOR_ACTIVITY_SPLIT_MIN_WIDTH,
    tutorActivityActions,
    tutorActivityDisplayModes,
    type ApiTutorActivity,
} from "../../../../adapters/tutor/Students/tutorStudentActivityAdapter"
import { finalizeSilentBody, redoLiveActivityBody } from "../../../../adapters/tutor/Live/tutorLiveActivityAdapter"
import { type ApiSchoolSubject } from "../../../../adapters/parent/shared/schoolSubjectAdapter"
import {
    liveActivityRequiresQuiz,
    toUITutorQuizReview,
} from "../../../../view-models/tutor/Live/tutorLiveCenterViewModel"
import { selectAccount, selectDisplayedStudent } from "../../../../view-models/shared/displayedStudent"
import { apiErrorMessage } from "../../../hooks/apiErrorMessage"
import { type GestureName, useGestureEmitter } from "../../../hooks/gestureTracking"
import { type ApiGenerateSourceActivity } from "../../../../adapters/tutor/Students/tutorGenerateActivityAdapter"
import { type UIActivityDisplayMode } from "../../../types"
import { type UICorrectionFeedback } from "../../../types/studentActivity"
import { type UITutorActivityAction, type UITutorActivityDialog } from "../../../types/tutorEleves"
import { type OpenGeneration, type TutorStudentId } from "./tutorStudentTabs"
import { QUIZ_REVIEW_TEXTS, useTutorActivityQuizReview } from "./useTutorActivityQuizReview"

/**
 * Les mots de la fiche hors live : ceux du legacy, sans ✅ / ⚠️ — les mêmes que le centre du live
 * (`CENTER_TEXTS`, `Room/useTutorLiveCenterActions.ts`), recopiés pour ne pas tirer la salle dans Mes élèves.
 */
export const TUTOR_ACTIVITY_TEXTS = {
    submitted: (firstname: string | null | undefined) => `Activité soumise pour ${firstname || "l'élève"}`,
    redone: "La même activité a été ajoutée à la liste de l'élève",
    emptyTitle: "Le titre ne peut pas être vide",
    titleSaved: "Titre modifié avec succès",
    quizRemoved: (name: string) => `Quiz retiré : ${name} voit l'énoncé`,
    quizRemoveFailed: "Le quiz n'a pas pu être retiré, réessaie.",
    failed: "Une erreur est survenue. Réessaie dans un instant.",
    loadFailed: "Impossible d'ouvrir cette activité. Réessaie dans un instant.",
    feedbackFailed: QUIZ_REVIEW_TEXTS.feedbackFailed,
}

const SURFACE = "tutor_activity_details"

/** Ce que chaque geste envoie au serveur, dans l'ordre. Hors live, aucun message de salle. */
type TutorActivityDetailsEquivalence = { gesture: string; services: string[] }

export const TUTOR_ACTIVITY_DETAILS_EQUIVALENCE: TutorActivityDetailsEquivalence[] = [
    { gesture: "open", services: ["getActivities"] },
    { gesture: "submit", services: ["finalizeActivitySilent", "getStudent", "getActivities"] },
    { gesture: "validate", services: ["tutorValidateActivity", "getActivities"] },
    { gesture: "redo", services: ["activityTodoAsTutor"] },
    { gesture: "archive", services: ["getActivity", "deleteActivitiesTodo"] },
    { gesture: "restore", services: ["restoreArchivedActivities", "getStudent"] },
    { gesture: "editTitle", services: ["updateActivityTodo"] },
    // La relecture de l'activité s'ajoute au legacy : sa fiche lisait l'activité dans la liste, que
    // `getStudent` rafraîchissait ; celle-ci tient sa propre lecture.
    { gesture: "removeQuiz", services: ["deletePrerequisiteQuiz", "getStudent", "getActivities"] },
    { gesture: "correctionReview", services: ["submitActivityCorrectionFeedback"] },
    // La vue « Quizz » (`PrerequisiteQuizDisplay`) : l'avis, une fois ; « Signaler » une question.
    { gesture: "quizFeedback", services: ["giveQuizFeedback"] },
    { gesture: "signalQuestion", services: ["signalQuizQuestion"] },
]

type Notice = { message: string; tone: SnackbarTone }

export type UseTutorActivityDetailsArgs = {
    studentId: TutorStudentId | null
    /** « Générer... » : ouvre la génération à partir de l'exercice. Absent : pas de geste. */
    openGeneration?: OpenGeneration
}

export type TutorActivityDetails = {
    /** Ouvre la fiche. Une ligne déjà lue (les archives) s'affiche tout de suite, relue ensuite. */
    open: (
        activityId: TutorStudentId,
        options?: {
            fromArchived?: boolean
            seed?: ApiTutorActivity | null
            onRestored?: () => void
        }
    ) => void
    modal: TutorActivityDetailsModalProps
    /** L'activité ouverte (refermée par la croix, ou par son archivage) ; `null` : aucune. */
    activityId: TutorStudentId | null
    /** Sa lecture (`getActivities`, `with_logs`) : le centre du cadre du live la montre comme le live. */
    raw: ApiTutorActivity | null
    notice: SnackbarProps
    /** Un accusé venu d'ailleurs (l'onglet), dans la même fente : un seul bandeau à la fois. */
    notify: (message: string, tone: SnackbarTone) => void
}

/* eslint-disable @typescript-eslint/no-explicit-any */
export function useTutorActivityDetails(args: UseTutorActivityDetailsArgs): TutorActivityDetails {
    const { studentId, openGeneration } = args
    const dispatch = useDispatch() as unknown as (action: never) => unknown
    const emit = useGestureEmitter()
    const { width } = useWindowDimensions()
    const wide = width > TUTOR_ACTIVITY_SPLIT_MIN_WIDTH

    const subjects = useSelector((s: any) => s?.data?.staticData?.school_subjects as ApiSchoolSubject[] | undefined)
    const templates = useSelector(
        (s: any) => s?.data?.staticData?.activity_templates as { id?: string | number }[] | undefined
    )
    const pendingAttachments = useSelector((s: any) => s?.data?.pendingAttachments?.byActivityId)
    const meId = useSelector((s: any) => selectAccount(s)?.id as number | undefined)
    const student = useSelector((s: any) => (studentId == null ? null : selectDisplayedStudent(s, studentId)))

    const [activityId, setActivityId] = useState<TutorStudentId | null>(null)
    const [fromArchived, setFromArchived] = useState(false)
    const [raw, setRaw] = useState<ApiTutorActivity | null>(null)
    const [loadError, setLoadError] = useState<string | null>(null)
    const [mode, setMode] = useState<UIActivityDisplayMode | null>(null)
    const [dialog, setDialog] = useState<UITutorActivityDialog | null>(null)
    const [submitting, setSubmitting] = useState<UITutorActivityAction | null>(null)
    const [title, setTitle] = useState<{ value: string; saving: boolean } | null>(null)
    const [removingQuiz, setRemovingQuiz] = useState(false)
    const [correctionReview, setCorrectionReview] = useState<UICorrectionFeedback | null>(null)
    const [notice, setNotice] = useState<Notice | null>(null)
    const onRestoredRef = useRef<(() => void) | undefined>(undefined)

    const call = useCallback(
        <T = unknown>(service: unknown, params: Parameters<typeof apiCall>[2] = {}) =>
            apiCall<T>(dispatch, service as ApiService, params),
        [dispatch]
    )
    const notify = useCallback((message: string, tone: SnackbarTone) => setNotice({ message, tone }), [])
    const failWith = useCallback(
        (cause: unknown) => notify(apiErrorMessage(apiFailureOf(cause), TUTOR_ACTIVITY_TEXTS.failed), "danger"),
        [notify]
    )
    /** Une écriture mesurée : `submitted`, puis `succeeded` ou `failed`. */
    const measured = useCallback(
        <T>(gesture: GestureName, promise: () => Promise<T>) => {
            emit("submitted", { gesture, surface: SURFACE })
            return promise().then(
                (answer) => {
                    emit("succeeded", { gesture, surface: SURFACE })
                    return answer
                },
                (cause: unknown) => {
                    emit("failed", { gesture, surface: SURFACE }, apiFailureOf(cause) ?? cause)
                    throw cause
                }
            )
        },
        [emit]
    )

    // ── La lecture ─────────────────────────────────────────────────────────────────────────────────
    // La dernière lecture partie : une réponse d'une activité refermée ou remplacée ne s'affiche pas.
    const readRef = useRef(0)
    const latest = useRef({ activityId, fromArchived, raw, studentId, meId, student, templates, subjects })
    latest.current = { activityId, fromArchived, raw, studentId, meId, student, templates, subjects }

    const refresh = useCallback(() => {
        const { activityId: id, fromArchived: archived } = latest.current
        if (id == null) return
        const read = ++readRef.current
        call<ApiTutorActivity[]>(activitiesService.getActivities, {
            urlParams: { with_logs: 1, activity_ids: id, include_archived: archived ? 1 : 0 },
        })
            .then((rows) => {
                if (read !== readRef.current) return
                const list = Array.isArray(rows) ? rows : []
                const found = list.find((row) => String(row?.id) === String(id)) ?? list[0] ?? null
                if (found != null) setRaw(found)
                setLoadError(null)
            })
            .catch((cause) => {
                if (read !== readRef.current) return
                setLoadError(apiErrorMessage(apiFailureOf(cause), TUTOR_ACTIVITY_TEXTS.loadFailed))
            })
    }, [call])

    const close = useCallback(() => {
        readRef.current += 1
        setActivityId(null)
        setRaw(null)
        setLoadError(null)
        setMode(null)
        setDialog(null)
        setSubmitting(null)
        setTitle(null)
        setCorrectionReview(null)
        onRestoredRef.current = undefined
    }, [])

    const open = useCallback<TutorActivityDetails["open"]>(
        (id, options) => {
            close()
            setActivityId(id)
            setFromArchived(options?.fromArchived === true)
            setRaw(options?.seed ?? null)
            onRestoredRef.current = options?.onRestored
            latest.current = { ...latest.current, activityId: id, fromArchived: options?.fromArchived === true }
            refresh()
        },
        [close, refresh]
    )

    // Le champion corrige : la fiche se relit, comme `TodoModal` (`pending_review` ou `correction_generating`).
    useCorrectionPolling(
        activityId != null && (raw?.status === "pending_review" || raw?.correction_generating === true),
        refresh
    )

    // L'activité a QUITTÉ les listes de l'élève (archivée d'ailleurs) : la fiche se ferme (`TodoModal:316-335`).
    // Une transition, pas une absence : une terminée lue hors de ces listes (l'historique déplié, une pièce
    // ancienne du chat, les archives) n'y a jamais été, et reste ouverte.
    const listedRef = useRef(false)
    useEffect(() => {
        listedRef.current = false
    }, [activityId])
    useEffect(() => {
        if (activityId == null || raw == null) return
        const listed = activityStillListed(student, activityId)
        if (listed) listedRef.current = true
        else if (listedRef.current) close()
    }, [activityId, close, raw, student])

    // ── L'avis du tuteur sur la correction du champion ──────────────────────────────────────────────────
    const completed = raw?.correction_status === "completed"
    const rawId = raw?.id
    useEffect(() => {
        if (rawId == null || !completed) {
            setCorrectionReview(null)
            return
        }
        let current = true
        call<{ feedback_type?: string | null }>(activitiesService.getActivityCorrectionFeedback, {
            routeParams: { activityId: rawId },
        })
            .then((answer) => {
                const type = answer?.feedback_type
                if (current) setCorrectionReview(type === "positive" || type === "negative" ? type : null)
            })
            .catch(() => undefined)
        return () => {
            current = false
        }
    }, [call, completed, rawId])

    const sendingReviewRef = useRef(false)
    const [reviewSending, setReviewSending] = useState(false)
    const onCorrectionReview = useCallback(
        (review: UICorrectionFeedback) => {
            const id = latest.current.raw?.id
            if (id == null || sendingReviewRef.current) return
            amplitude.track("checkpoint.feedback.button_click")
            sendingReviewRef.current = true
            setReviewSending(true)
            setCorrectionReview(review)
            measured("activity.correction_review", () =>
                call(activitiesService.submitActivityCorrectionFeedback, {
                    data: { activityId: id, feedbackType: review },
                })
            )
                .catch((cause) =>
                    notify(apiErrorMessage(apiFailureOf(cause), TUTOR_ACTIVITY_TEXTS.feedbackFailed), "danger")
                )
                .finally(() => {
                    sendingReviewRef.current = false
                    setReviewSending(false)
                })
        },
        [call, measured, notify]
    )

    // ── Les gestes ─────────────────────────────────────────────────────────────────────────────────
    const rereadStudent = useCallback(() => {
        const id = latest.current.studentId
        if (id != null) call(studentsService.getStudent, { routeParams: { id } }).catch(() => undefined)
    }, [call])

    const submit = useCallback(() => {
        const { raw: activity, student: who, studentId: id } = latest.current
        if (activity?.id == null || id == null) return
        const pending = buildPendingAttachmentsBody(
            (pendingAttachments as Record<string, unknown> | null | undefined)?.[String(activity.id)]
        ) as unknown[] | undefined
        setSubmitting("submit")
        measured("activity.submit", () =>
            call(activitiesService.finalizeActivitySilent, {
                routeParams: { id: activity.id },
                data: finalizeSilentBody(activity, pending),
            })
        )
            .then(() => {
                notify(TUTOR_ACTIVITY_TEXTS.submitted(who?.firstname as string | undefined), "success")
                rereadStudent()
                refresh()
            })
            .catch(failWith)
            .finally(() => setSubmitting(null))
    }, [call, failWith, measured, notify, pendingAttachments, refresh, rereadStudent])

    const validate = useCallback(() => {
        const { raw: activity, meId: me } = latest.current
        if (activity?.id == null) return
        setSubmitting("validate")
        // `reviewer` part comme dans l'ancien ; le service l'ignore (`activitiesService:73`).
        measured("activity.validate", () =>
            call(activitiesService.tutorValidateActivity, { routeParams: { id: activity.id, reviewer: me } })
        )
            .then(() => refresh())
            .catch(failWith)
            .finally(() => setSubmitting(null))
    }, [call, failWith, measured, refresh])

    const redo = useCallback(() => {
        const { raw: activity, studentId: id, templates: known, subjects: catalog } = latest.current
        if (activity == null || id == null) return
        const templateId = known?.find((t) => String(t?.id) === String(activity.activity_template_id))?.id
        const subjectId = catalog?.find((s) => String(s?.id) === String(activity.school_subject_id))?.id
        setSubmitting("redo")
        measured("activity.redo", () =>
            call(activitiesService.activityTodoAsTutor, {
                routeParams: { id },
                data: redoLiveActivityBody(activity, { activityTemplateId: templateId, schoolSubjectId: subjectId }),
            })
        )
            .then(() => {
                notify(TUTOR_ACTIVITY_TEXTS.redone, "success")
                close()
            })
            .catch(failWith)
            .finally(() => setSubmitting(null))
    }, [call, close, failWith, measured, notify])

    const askArchive = useCallback(() => {
        const { raw: activity, meId: me } = latest.current
        if (activity?.id == null) return
        setSubmitting("archive")
        call<ApiTutorActivity>(activitiesService.getActivity, { routeParams: { id: activity.id } })
            .then((fresh) => {
                const answer = archiveAnswerFor(fresh, me)
                if (answer === "archive") emit("opened", { gesture: "activity.archive", surface: SURFACE })
                setDialog(answer)
            })
            .catch(failWith)
            .finally(() => setSubmitting(null))
    }, [call, emit, failWith])

    const archive = useCallback(() => {
        const { raw: activity, studentId: id } = latest.current
        if (activity?.id == null || id == null) return
        setSubmitting("archive")
        measured("activity.archive", () =>
            call(activitiesService.deleteActivitiesTodo, {
                routeParams: { student: id },
                data: { activity_ids: [activity.id] },
            })
        )
            .then(() => close())
            .catch(failWith)
            .finally(() => setSubmitting(null))
    }, [call, close, failWith, measured])

    const restore = useCallback(() => {
        const { raw: activity, studentId: id } = latest.current
        if (activity?.id == null || id == null) return
        setSubmitting("restore")
        const onRestored = onRestoredRef.current
        measured("activity.restore", () =>
            call(activitiesService.restoreArchivedActivities, {
                routeParams: { student: id },
                data: { activity_ids: [activity.id] },
            })
        )
            .then(() => {
                rereadStudent()
                close()
                onRestored?.()
            })
            .catch(failWith)
            .finally(() => setSubmitting(null))
    }, [call, close, failWith, measured, rereadStudent])

    const editTitle = useCallback(() => {
        const activity = latest.current.raw
        emit("opened", { gesture: "activity.edit_title", surface: SURFACE })
        setTitle({ value: activity?.material?.title ?? activity?.title ?? "", saving: false })
    }, [emit])

    const saveTitle = useCallback(() => {
        const activity = latest.current.raw
        if (title == null || activity?.id == null) return
        const trimmed = title.value.trim()
        if (trimmed === "") {
            notify(TUTOR_ACTIVITY_TEXTS.emptyTitle, "danger")
            return
        }
        setTitle({ ...title, saving: true })
        measured("activity.edit_title", () =>
            call(activitiesService.updateActivityTodo, {
                routeParams: { id: activity.id },
                data: { material_title: trimmed },
            })
        )
            .then(() => {
                notify(TUTOR_ACTIVITY_TEXTS.titleSaved, "success")
                setTitle(null)
                // Le réducteur range le titre dans les listes ; la fiche, qui tient sa lecture, le prend d'ici.
                setRaw((was) => (was != null ? { ...was, material: { ...was.material, title: trimmed } } : was))
            })
            .catch((cause) => {
                setTitle((was) => (was != null ? { ...was, saving: false } : was))
                failWith(cause)
            })
    }, [call, failWith, measured, notify, title])

    const removeQuiz = useCallback(() => {
        const { raw: activity, student: who } = latest.current
        if (removingQuiz || activity?.id == null) return
        setRemovingQuiz(true)
        // Hors live (pas de séance), la vue du masteur se relit par l'élève (`QuizFirstMention.refresh`).
        const name = (who?.display_name as string | undefined) || "l'élève"
        measured("activity.remove_quiz", () =>
            call(activitiesService.deletePrerequisiteQuiz, { routeParams: { activityId: activity.id } })
        )
            .then(() => {
                rereadStudent()
                refresh()
                notify(TUTOR_ACTIVITY_TEXTS.quizRemoved(name), "success")
            })
            .catch((cause) => {
                rereadStudent()
                notify(apiFailureOf(cause)?.message || TUTOR_ACTIVITY_TEXTS.quizRemoveFailed, "danger")
            })
            .finally(() => setRemovingQuiz(false))
    }, [call, measured, notify, refresh, removingQuiz, rereadStudent])

    const generate = useCallback(() => {
        const { raw: activity, studentId: id } = latest.current
        if (openGeneration == null || activity?.id == null || id == null) return
        openGeneration({
            kind: "fromActivity",
            studentId: id,
            activityId: activity.id,
            activity: activity as ApiGenerateSourceActivity,
        })
    }, [openGeneration])

    const onAction = useCallback(
        (action: UITutorActivityAction) => {
            switch (action) {
                case "submit":
                    return submit()
                case "validate":
                    emit("opened", { gesture: "activity.validate", surface: SURFACE })
                    return setDialog("validate")
                case "redo":
                    return redo()
                case "archive":
                    return askArchive()
                case "restore":
                    return restore()
                case "editTitle":
                    return editTitle()
                case "generate":
                    return generate()
                case "show":
                    // « Afficher à l'élève » demande la salle : il n'est jamais offert hors live.
                    return undefined
            }
        },
        [askArchive, editTitle, emit, generate, redo, restore, submit]
    )

    const onConfirmDialog = useCallback(() => {
        const open = dialog
        setDialog(null)
        if (open === "archive") archive()
        else if (open === "validate") validate()
    }, [archive, dialog, validate])

    // ── Les props ──────────────────────────────────────────────────────────────────────────────────
    const subject = useMemo(
        () => (subjects ?? []).find((s) => String(s?.id) === String(raw?.school_subject_id)) ?? null,
        [raw?.school_subject_id, subjects]
    )
    const activity = useMemo(() => toUITutorActivityDetails(raw, { subject, cleanUrl: cleanMediaUrl }), [raw, subject])
    const quizReview = useMemo(() => (raw?.has_prerequisite_quiz ? toUITutorQuizReview(raw) : null), [raw])
    // L'avis sur le quiz et « Signaler » une question, dans la vue « Quizz » (`PrerequisiteQuizDisplay`).
    const openedGesture = useCallback((gesture: GestureName) => emit("opened", { gesture, surface: SURFACE }), [emit])
    const quizReviewGestures = useTutorActivityQuizReview(raw?.has_prerequisite_quiz ? (raw as never) : null, notify, {
        measured,
        opened: openedGesture,
    })
    const modes = useMemo(() => tutorActivityDisplayModes(raw, wide), [raw, wide])
    const shownMode = mode != null && modes.includes(mode) ? mode : defaultTutorActivityDisplayMode(raw, wide)
    const hasCorrectionPoints = (activity?.work?.correctionPoints?.length ?? 0) > 0
    const visible = activityId != null

    const modal: TutorActivityDetailsModalProps = {
        visible,
        onClose: close,
        activity,
        // Ouverte dès le geste : tant que l'activité n'est pas lue (et que la lecture n'a pas échoué), sa forme.
        loading: visible && raw == null && loadError == null,
        error: raw == null ? loadError : null,
        modes,
        mode: shownMode,
        onModeChange: setMode,
        studentName: (student?.display_name as string | undefined) ?? (student?.firstname as string | undefined) ?? "",
        actions: tutorActivityActions(raw, { fromArchived, canGenerate: openGeneration != null }),
        onAction,
        submitting,
        quizFirst: liveActivityRequiresQuiz(raw) ? { onRemove: removeQuiz, removing: removingQuiz } : null,
        quizReview,
        quizFeedback: quizReviewGestures.feedback,
        onSignalQuizQuestion: quizReviewGestures.onSignalQuestion,
        quizSignal: quizReviewGestures.signal,
        correctionFeedback: hasCorrectionPoints
            ? { value: correctionReview, onChange: onCorrectionReview, submitting: reviewSending }
            : null,
        dialog,
        onConfirmDialog,
        onCancelDialog: () => setDialog(null),
        titleEditor:
            title != null
                ? {
                      value: title.value,
                      saving: title.saving,
                      onChange: (value) => setTitle((was) => (was != null ? { ...was, value } : was)),
                      onSave: saveTitle,
                      onCancel: () => setTitle(null),
                  }
                : null,
    }

    return {
        open,
        modal,
        activityId,
        raw,
        notice: {
            visible: notice != null,
            message: notice?.message ?? "",
            tone: notice?.tone,
            onDismiss: () => setNotice(null),
        },
        notify,
    }
}
/* eslint-enable @typescript-eslint/no-explicit-any */
